import { useCallback, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  Check,
  CircleSlash,
  Hammer,
  RotateCwSquare,
  TriangleAlert,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HoverCard, HoverCardTrigger } from '@/components/ui/hover-card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { getClass } from 'app/frontend/base/services/characterValues'
import { getTimelineSpellNameById } from 'app/frontend/base/services/raidPlanData'
import { RaidPlanActor, RaidPlanCast } from 'app/frontend/types/custom/RaidPlan'
import CharacterClass, {
  characterClasses,
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import useObservedWidth from '../hooks/useObservedWidth'
import useTimelineTicks from '../hooks/useTimelineTicks'
import useVisibleTimelineTicks from '../hooks/useVisibleTimelineTicks'
import { formatTimelineTimestamp } from './timelineMath'
import SpellIcon from './SpellIcon'
import { useRef } from 'react'
import Card from 'app/frontend/base/components/Card'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { ResolvedAssignmentParticipant } from '../../TeamEvent/components/eventActivityTimelineResolver'
import { ResolvedAssignmentEntity } from '../../TeamEvent/components/eventActivityTimelineResolver'
import { NOTE_ICON_URL } from '../config/generalAbilities'
import { EncounterAssignmentEntityOverride } from '../../TeamEvent/components/eventActivityPlanOverrides'
import CastTargetsHoverCardContent from './CastTargetsHoverCardContent'

export type ReadonlyActorTimelineEntry = {
  actor: RaidPlanActor
  label: string
  plannedLabel?: string
  isUnresolved: boolean
  isIgnored?: boolean
  hasReplacementOverride?: boolean
  resolvedCharacterId?: number
  isOverridden?: boolean
}

type Props = {
  actors: ReadonlyActorTimelineEntry[]
  generalCasts?: RaidPlanCast[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  onActorOverrideSelect?: (selection: {
    actorUuid: string
    characterId: number
  }) => void
  onActorIgnoreSelect?: (actorUuid: string) => void
  onUnresolvedActorClick?: (actor: RaidPlanActor) => void
  resolvedCastTargetsByCastId?: Record<string, ResolvedAssignmentEntity[]>
  onCastTargetOverrideSelect?: (
    selection:
      | {
          castId: string
          entityId: string
          override: EncounterAssignmentEntityOverride
        }
      | {
          castId: string
          entityId: string
          override: EncounterAssignmentEntityOverride
        }[],
  ) => void
  canEditCastTargets?: boolean
  maxTime: number
  className?: string
}

const ROW_HEIGHT = 20
const ROW_GAP = 0
const ICON_SIZE = 20
const TICK_LABEL_AREA_HEIGHT = 20

const ActorTimelineReadonlyRow = ({
  actors,
  generalCasts,
  selectedParticipants,
  onActorOverrideSelect,
  onActorIgnoreSelect,
  onUnresolvedActorClick,
  resolvedCastTargetsByCastId = {},
  onCastTargetOverrideSelect,
  canEditCastTargets = false,
  maxTime,
  className,
}: Props) => {
  const timelineRef = useRef<HTMLDivElement>(null)
  const [openActorUuid, setOpenActorUuid] = useState<string | null>(null)
  const timelineWidth = useObservedWidth(timelineRef)
  const safeMaxTime = Math.max(maxTime, 1)
  const visibleRange = useMemo(
    () => ({ start: 0, end: safeMaxTime }),
    [safeMaxTime],
  )
  const { minorTickTimes, minuteTickTimes } = useTimelineTicks({
    maxTime: safeMaxTime,
    phaseDividers: [],
  })
  const { visibleMinorTickTimes, visibleMinuteTickTimes } =
    useVisibleTimelineTicks({
      minorTickTimes,
      minuteTickTimes,
      visibleRange,
    })
  const toX = useCallback(
    (time: number) =>
      ((time - visibleRange.start) / safeMaxTime) * timelineWidth,
    [safeMaxTime, timelineWidth, visibleRange.start],
  )
  const rowsHeight =
    (actors.length + ((generalCasts?.length ?? 0) > 0 ? 1 : 0)) * ROW_HEIGHT +
    Math.max(actors.length + ((generalCasts?.length ?? 0) > 0 ? 1 : 0) - 1, 0) *
      ROW_GAP
  const svgHeight = rowsHeight + TICK_LABEL_AREA_HEIGHT
  const hasGeneralRow = (generalCasts?.length ?? 0) > 0
  const generalRowIndex = actors.length
  const minuteTickLabelTimes = useMemo(
    () => minuteTickTimes.filter((tickAt) => tickAt + 15 <= safeMaxTime),
    [minuteTickTimes, safeMaxTime],
  )
  const renderCastIcon = ({
    cast,
    top,
    isIgnored,
    testId,
  }: {
    cast: RaidPlanCast
    top: number
    isIgnored: boolean
    testId: string
  }) => {
    const abilityName =
      cast.spell_id === null
        ? 'Message'
        : getTimelineSpellNameById()[cast.spell_id] ?? `Spell ${cast.spell_id}`
    const message = cast.message?.trim() ?? ''
    const resolvedTargets = resolvedCastTargetsByCastId[cast.id] ?? []
    const targetGroups = cast.targets ?? []
    const hasTargets = targetGroups.some((group) => group.length > 0)
    const hasTargetIssues = resolvedTargets.some((entity) =>
      Boolean(entity.issue),
    )
    const icon = (
      <span
        className='absolute pointer-events-auto cursor-default'
        style={{
          left: toX(cast.at),
          top,
          width: ICON_SIZE,
          height: ICON_SIZE,
        }}
        data-testid={testId}
      >
        <span className='relative inline-flex'>
          {cast.spell_id === null ? (
            <img
              src={NOTE_ICON_URL}
              className={cn(
                'h-5 w-5 rounded border border-border',
                hasTargetIssues && 'border-warning-foreground border-2',
                isIgnored && 'grayscale opacity-60',
              )}
            />
          ) : (
            <SpellIcon
              spellId={cast.spell_id}
              className={cn(
                'h-5 w-5 rounded border',
                hasTargetIssues && 'border-warning-foreground border-2',
                isIgnored && 'grayscale opacity-60',
              )}
            />
          )}
          {hasTargetIssues ? (
            <span className='absolute inset-0 inline-flex items-center justify-center pointer-events-none'>
              <TriangleAlert className='h-4 w-4 rounded-[4px] text-warning-foreground bg-accent/40' />
            </span>
          ) : null}
        </span>
      </span>
    )
    if (!hasTargets) {
      return (
        <Tooltip key={cast.id}>
          <TooltipTrigger asChild>{icon}</TooltipTrigger>
          <TooltipContent side='top' className='text-center'>
            <div>{abilityName}</div>
            {message.length ? (
              <div className='text-xs text-muted-foreground text-center whitespace-pre-line'>
                {message}
              </div>
            ) : null}
            <div className='text-xs text-muted-foreground text-center'>
              {formatTimelineTimestamp(cast.at)}
            </div>
          </TooltipContent>
        </Tooltip>
      )
    }
    return (
      <HoverCard key={cast.id} openDelay={100}>
        <HoverCardTrigger asChild>{icon}</HoverCardTrigger>
        <CastTargetsHoverCardContent
          cast={cast}
          abilityName={abilityName}
          resolvedTargets={resolvedTargets}
          selectedParticipants={selectedParticipants}
          canEditCastTargets={canEditCastTargets}
          onCastTargetOverrideSelect={onCastTargetOverrideSelect}
        />
      </HoverCard>
    )
  }
  return (
    <div className={cn('flex gap-3', className)}>
      <Card className='space-y-0 p-0 mb-5 w-40 max-w-40 shrink-0'>
        {actors.map((entry, actorIndex) => {
          const className = getClass(entry.actor.class_id.toString())
          const characterClass = characterClasses.includes(
            className as CharacterClass,
          )
            ? (className as CharacterClass)
            : undefined
          const overrideOptions = selectedParticipants.filter((participant) => {
            return entry.actor.spec_id !== null
              ? participant.specId === entry.actor.spec_id
              : participant.classId === entry.actor.class_id
          })
          const canOpenOverridePopover =
            Boolean(onActorIgnoreSelect) ||
            (Boolean(onActorOverrideSelect) && overrideOptions.length > 0)
          const opensReplacementDialog =
            entry.isUnresolved || entry.hasReplacementOverride === true
          const isIgnoredActor = entry.isIgnored === true
          const hasFallbackMatch =
            !opensReplacementDialog &&
            !entry.isOverridden &&
            typeof entry.actor.preferred_character_id === 'number' &&
            typeof entry.resolvedCharacterId === 'number' &&
            entry.actor.preferred_character_id !== entry.resolvedCharacterId
          const rowContent = (
            <div
              className={cn(
                'h-5 flex items-center gap-1 px-2 text-sm truncate',
                entry.isUnresolved &&
                  !isIgnoredActor &&
                  'bg-warning border-y border-warning-foreground',
                isIgnoredActor && 'grayscale opacity-70',
                opensReplacementDialog || canOpenOverridePopover
                  ? 'cursor-pointer hover:bg-accent'
                  : '',
              )}
            >
              <CharacterClassIcon
                characterClass={characterClass}
                size='small'
                style='round'
              />
              <span
                className={cn(
                  'truncate grow',
                  !entry.isUnresolved && characterClass
                    ? classColours[characterClass].text
                    : 'text-muted-foreground',
                )}
              >
                {entry.label}
              </span>
              {isIgnoredActor ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='shrink-0'>
                      <CircleSlash
                        className='h-3.5 w-3.5 text-muted-foreground shrink-0'
                        data-testid='cooldown-actor-ignored'
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {`These cooldowns have been marked as ignored`}
                  </TooltipContent>
                </Tooltip>
              ) : entry.isOverridden ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='shrink-0'>
                      <Hammer className='h-4 w-4 text-info-foreground' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Manually edited</TooltipContent>
                </Tooltip>
              ) : entry.isUnresolved ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='shrink-0'>
                      <TriangleAlert
                        className='h-3.5 w-3.5 text-warning-foreground shrink-0'
                        data-testid='cooldown-actor-unresolved'
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {`No match found for ${
                      entry.plannedLabel ?? entry.actor.label
                    }`}
                  </TooltipContent>
                </Tooltip>
              ) : hasFallbackMatch ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='shrink-0'>
                      <RotateCwSquare className='h-4 w-4 text-info-foreground' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <>
                      Replaces{' '}
                      <span
                        className={
                          characterClass
                            ? classColours[characterClass].text
                            : undefined
                        }
                      >
                        {entry.plannedLabel ?? entry.actor.label}
                      </span>{' '}
                      in the plan
                    </>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          )
          if (opensReplacementDialog) {
            if (!onUnresolvedActorClick) {
              return (
                <div key={`${entry.label}:${actorIndex}`}>{rowContent}</div>
              )
            }
            return (
              <button
                key={`${entry.label}:${actorIndex}`}
                type='button'
                className='block w-full text-left p-0 m-0 border-0 bg-transparent align-top'
                onClick={() => onUnresolvedActorClick(entry.actor)}
                data-testid={`actor-unresolved-trigger-${entry.actor.uuid}`}
              >
                {rowContent}
              </button>
            )
          }
          if (!canOpenOverridePopover) {
            return <div key={`${entry.label}:${actorIndex}`}>{rowContent}</div>
          }
          return (
            <Popover
              key={`${entry.label}:${actorIndex}`}
              open={openActorUuid === entry.actor.uuid}
              onOpenChange={(open) => {
                setOpenActorUuid(open ? entry.actor.uuid : null)
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='block w-full text-left p-0 m-0 border-0 bg-transparent align-top'
                  data-testid={`actor-override-trigger-${entry.actor.uuid}`}
                >
                  {rowContent}
                </button>
              </PopoverTrigger>
              <PopoverContent className='w-fit p-0' align='start'>
                <Command>
                  <CommandList>
                    <CommandEmpty>No options found</CommandEmpty>
                    <CommandGroup>
                      {overrideOptions.map((participant) => {
                        const isSelected =
                          !isIgnoredActor &&
                          typeof entry.resolvedCharacterId === 'number' &&
                          participant.characterId === entry.resolvedCharacterId
                        return (
                          <CommandItem
                            key={participant.participantId}
                            value={`${participant.name} ${participant.role}`}
                            onSelect={() => {
                              if (!onActorOverrideSelect) return
                              onActorOverrideSelect({
                                actorUuid: entry.actor.uuid,
                                characterId: participant.characterId,
                              })
                              setOpenActorUuid(null)
                            }}
                            className='flex items-center gap-2'
                          >
                            <CharacterClassIcon
                              characterClass={participant.characterClass}
                              size='small'
                            />
                            <span
                              className={
                                classColours[participant.characterClass].text
                              }
                            >
                              {participant.name}
                            </span>
                            <Check
                              className={cn(
                                'ml-auto h-4 w-4',
                                isSelected ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                          </CommandItem>
                        )
                      })}
                      {onActorIgnoreSelect ? (
                        <CommandItem
                          value='ignore'
                          onSelect={() => {
                            onActorIgnoreSelect(entry.actor.uuid)
                            setOpenActorUuid(null)
                          }}
                          className='flex items-center justify-start text-muted-foreground gap-2'
                        >
                          <CircleSlash className='h-4 w-4' />
                          <span>Ignore</span>
                          <Check
                            className={cn(
                              'ml-auto h-4 w-4',
                              isIgnoredActor ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      ) : null}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )
        })}
        {hasGeneralRow ? (
          <div>
            <div className='h-5 flex items-center gap-1 px-2 text-sm truncate text-muted-foreground'>
              <span className='truncate grow'>Everyone</span>
            </div>
          </div>
        ) : null}
      </Card>

      <div
        className='relative grow min-w-0'
        data-testid='readonly-actor-timeline-row'
        ref={timelineRef}
      >
        <svg
          className='block w-full'
          width='100%'
          height={svgHeight}
          viewBox={`0 0 ${timelineWidth} ${svgHeight}`}
        >
          <g className='text-card'>
            <rect
              x={0}
              y={0}
              width={timelineWidth}
              height={rowsHeight}
              fill='currentColor'
              rx={4}
              ry={4}
            />
          </g>
          <g className='text-border'>
            <rect
              x={0}
              y={0}
              width={timelineWidth}
              height={rowsHeight}
              fill='transparent'
              stroke='currentColor'
              strokeWidth={1.25}
              vectorEffect='non-scaling-stroke'
              rx={8}
              ry={8}
            />
          </g>

          <g className='text-border'>
            {visibleMinorTickTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <line
                  key={`minor-tick-${tickAt}`}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={rowsHeight}
                  stroke='currentColor'
                  strokeWidth={1}
                  opacity={0.45}
                />
              )
            })}
          </g>

          <g className='text-foreground'>
            {visibleMinuteTickTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <line
                  key={`minute-tick-${tickAt}`}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={rowsHeight}
                  stroke='currentColor'
                  strokeWidth={1}
                  opacity={0.3}
                />
              )
            })}
          </g>

          {actors.map((entry, rowIndex) => {
            const y = rowIndex * (ROW_HEIGHT + ROW_GAP)
            return (
              <g key={`${entry.label}:${rowIndex}`}>
                <rect
                  x={0}
                  y={y}
                  width={timelineWidth}
                  height={ROW_HEIGHT}
                  fill='transparent'
                />
              </g>
            )
          })}
          {hasGeneralRow ? (
            <g key='everyone-row'>
              <rect
                x={0}
                y={generalRowIndex * (ROW_HEIGHT + ROW_GAP)}
                width={timelineWidth}
                height={ROW_HEIGHT}
                fill='transparent'
              />
            </g>
          ) : null}

          <g className='text-muted-foreground'>
            {minuteTickLabelTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <text
                  key={`minute-tick-label-${tickAt}`}
                  x={x}
                  y={rowsHeight + 14}
                  fill='currentColor'
                  fontSize={12}
                  textAnchor='middle'
                >
                  {formatTimelineTimestamp(tickAt)}
                </text>
              )
            })}
          </g>
        </svg>
        <div className='absolute inset-0 pointer-events-none'>
          {actors.map((entry, rowIndex) =>
            entry.actor.casts.map((cast) =>
              renderCastIcon({
                cast,
                top: rowIndex * (ROW_HEIGHT + ROW_GAP),
                isIgnored: entry.isIgnored === true,
                testId: 'readonly-actor-cast-icon',
              }),
            ),
          )}
          {(generalCasts ?? []).map((cast) =>
            renderCastIcon({
              cast,
              top: generalRowIndex * (ROW_HEIGHT + ROW_GAP),
              isIgnored: false,
              testId: 'readonly-general-cast-icon',
            }),
          )}
        </div>
      </div>
    </div>
  )
}

export default ActorTimelineReadonlyRow
